import { FunctionComponent, useMemo, useState } from 'react'

import { Icons } from 'atoms'
import axios from 'axios'
import * as Linking from 'expo-linking'
import { Attachment } from 'integration/resources/knowledgeBase'
import { Box, HStack, Heading, VStack } from 'native-base'
import { StyleSheet, TouchableOpacity } from 'react-native'
import {
  formatDateStringToMask,
  formatNameAttachments,
  formatNameExtensionAttachments,
  formatSizeAttachments,
  selectIcon
} from 'src/utils'

type AttachmentFilesGridProps = {
  attachments: Attachment[]
  isDesktop?: boolean
}

export const AttachmentFilesGrid: FunctionComponent<AttachmentFilesGridProps> = ({
  attachments,
  isDesktop
}) => {
  const [disabledDownload, setDisableDownload] = useState(false)

  const rows = useMemo(() => {
    const chunked: Attachment[][] = []

    for (let i = 0; i < attachments.length; i += 3) {
      chunked.push(attachments.slice(i, i + 3))
    }

    return chunked
  }, [attachments])

  if (!rows.length) return <></>

  const createElementToDownload = (blob: any, name: string) => {
    const newUrlFile = window.URL.createObjectURL(blob)

    const element = document.createElement('a')

    element.setAttribute('href', newUrlFile)

    element.setAttribute('download', name)

    element.style.display = 'none'

    document.body.appendChild(element)

    element.click()

    document.body.removeChild(element)
  }

  const donwloadFile = async (file: { name: string; url: string }) => {
    if (!file) return

    setDisableDownload(true)

    const { url, name } = file

    try {
      const axiosConfig = { responseType: 'blob' }

      // @ts-ignore
      const { data: blob } = await axios.get(url, axiosConfig)

      createElementToDownload(blob, name)

      setDisableDownload(false)
    } catch (err) {
      setDisableDownload(false)

      console.log(err)
    }
  }

  return (
    <VStack mt={4} space={4} width="100%">
      {rows.map((row, rowIndex) => (
        <HStack key={`row-${rowIndex}`} space={4} alignItems="stretch">
          {row.map(({ fileName, url, uploaded_at, name, created_at }: Attachment) => {
            const extension = formatNameExtensionAttachments(url)

            const size = Number(formatSizeAttachments(name ? name : fileName))

            const IconComponent = Icons[selectIcon(extension)]

            const uploadedAt = formatDateStringToMask(
              (uploaded_at ? uploaded_at : created_at) ?? '',
              "dd'/'MM'/'yyyy"
            )

            const LIMIT_KB = 1000000

            const sizeFile = size ? (size < LIMIT_KB ? size + 'KB' : size + 'MB') : ''

            return (
              <Box key={fileName} flex={1}>
                <TouchableOpacity
                  style={styles.attachButton}
                  disabled={disabledDownload}
                  onPress={() =>
                    isDesktop ? donwloadFile({ name: fileName, url }) : Linking.openURL(url)
                  }>
                  <HStack justifyContent="space-between" alignItems="center" flex={1}>
                    <HStack space={2} alignItems="center" width="4/5">
                      <Box flexShrink={0}>
                        <IconComponent />
                      </Box>
                      <VStack space={1} width="4/5">
                        <Heading
                          fontSize="14px"
                          lineHeight="18px"
                          fontWeight="bold"
                          color="gray.700"
                          isTruncated>
                          {formatNameAttachments(name ? name : fileName)}{' '}
                        </Heading>

                        <Heading
                          fontSize="12px"
                          lineHeight="16px"
                          fontWeight={400}
                          color="gray.500">
                          .{extension} {sizeFile} {'    '} {uploadedAt}
                        </Heading>
                      </VStack>
                    </HStack>

                    <Box width="1/5" alignItems="flex-end">
                      <Icons.Downloading size={5} color="gray.700" />
                    </Box>
                  </HStack>
                </TouchableOpacity>
              </Box>
            )
          })}
        </HStack>
      ))}
    </VStack>
  )
}

const styles = StyleSheet.create({
  attachButton: {
    backgroundColor: 'white',
    borderRadius: 20,
    minHeight: 70,
    padding: 16,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#C2C2C2'
  }
})
